import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { useScanWedge } from '../scanWedge';
import { scanSound } from '../scanSound';
import { GuidedTexts } from './guidedText';

// The Packeta handover at a P&P station (guided P&P phase 8, 2026-10-17; backend/ship_bulks.py). Fernando: Packeta
// goes in bulk under our own running number, and every parcel is RESCANNED at handover ("Safest is to rescan").
// Each scan goes into the open bulk (PK-0001 …) or is refused with the reason; the parcels processed since the last
// handover that were not scanned are listed, and closing past them needs a confirm.
// 3.11: a handover is for ONE pickup day and stays open until "Courier took them" — leaving keeps it, so Sunday's
// parcels and Monday's go together and a late courier gets the parcels packed meanwhile. From a round, "Back to
// packing" returns to it. A handover left open past its day asks first whether the courier took it.

interface Item { id: number; order_number: string; scanned_code: string; by_person: string; at: string }
interface Missing { order_id: string; order_number: string; tracking_number: string; processed_at: string | null; round_id?: number | null }
/** a parcel in the handover that must come out again (3.11): its label was cancelled / replaced, or its order left the round */
interface Problem { item_id: number; order_id: string; order_number: string; why: string }
interface Bulk { id: number; code: string; ship_date: string | null; opened_at: string }
interface State { bulk: Bulk | null; count: number; items: Item[]; missing: Missing[]; problems?: Problem[]; stale?: boolean }

async function detail(r: Response) {
  const d = await r.json().catch(() => ({} as any));
  return typeof d.detail === 'string' ? d.detail : d.detail?.message || `HTTP ${r.status}`;
}

export const shipDay = (iso: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'numeric' }) : '');
const hm = (iso: string) => new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const dayHm = (iso: string) => new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });

export default function PacketaHandover({ onBack, tx, fromRound = false }: { onBack: () => void; tx: GuidedTexts; fromRound?: boolean }) {
  const [st, setSt] = useState<State | null>(null);
  const [last, setLast] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ code: string; parcels: number; not_scanned: number } | null>(null);

  const load = useCallback(() => authFetch(`${API}/bulks/packeta`).then(async r => {
    if (r.ok) setSt(await r.json());
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const scan = useCallback(async (code: string) => {
    const r = await authFetch(`${API}/bulks/packeta/scan`, { method: 'POST', body: JSON.stringify({ code }) });
    if (!r.ok) { scanSound('error'); setLast({ ok: false, text: await detail(r) }); if (r.status === 409) load(); return; }
    const d = await r.json();
    setSt(d);
    scanSound('ok');
    setLast({ ok: true, text: `${d.scanned?.order_number || ''} ✓` });
  }, [load]);

  useScanWedge(code => scan(code), { enabled: !done && !st?.stale });    // the station scanner, with or without an Enter (28.10)

  const undo = async () => {
    const r = await authFetch(`${API}/bulks/packeta/undo`, { method: 'POST' });
    if (!r.ok) { setLast({ ok: false, text: await detail(r) }); return; }
    setSt(await r.json()); setLast({ ok: true, text: 'Last parcel taken out' });
  };
  const close = async (confirm: boolean) => {
    setBusy(true);
    try {
      const r = await authFetch(`${API}/bulks/packeta/close`, { method: 'POST', body: JSON.stringify({ confirm_missing: confirm }) });
      if (!r.ok) { setLast({ ok: false, text: await detail(r) }); return; }
      setDone(await r.json());
    } finally { setBusy(false); }
  };
  const takeOut = async (p: Problem) => {
    const r = await authFetch(`${API}/bulks/packeta/remove`, { method: 'POST', body: JSON.stringify({ item_id: p.item_id }) });
    if (!r.ok) { setLast({ ok: false, text: await detail(r) }); return; }
    setSt(await r.json()); setLast({ ok: true, text: `${p.order_number} taken out` });
  };
  const answerStale = async (taken: boolean) => {
    setBusy(true);
    try {
      const r = await authFetch(`${API}/bulks/packeta/stale`, { method: 'POST', body: JSON.stringify({ taken }) });
      if (!r.ok) { setLast({ ok: false, text: await detail(r) }); return; }
      setSt(await r.json()); setLast(null);
    } finally { setBusy(false); }
  };

  const backLabel = fromRound ? tx.packetaBack : tx.packetaDoneForNow;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-3xl font-black">{tx.packetaButton}</span>
        {st?.bulk && <span className="text-gray-300 text-xl">{tx.packetaFor(st.bulk.code, shipDay(st.bulk.ship_date))}</span>}
        <button onClick={onBack} className="ml-auto px-5 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 text-lg font-semibold">{done ? 'Done' : backLabel}</button>
      </div>

      {done ? (
        <div className="rounded-2xl border-2 border-emerald-600 bg-emerald-900/15 p-6 text-center space-y-3">
          <div className="text-3xl font-black text-emerald-200">{done.code}: {done.parcels} parcels handed over</div>
          {done.not_scanned > 0 && <div className="text-amber-200">{done.not_scanned} processed parcel(s) were not scanned — written on the handover</div>}
          <button onClick={onBack} className="px-6 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 text-lg">{backLabel}</button>
        </div>
      ) : st?.stale && st.bulk ? (
        // the handover's pickup day has passed and nobody said the courier took it
        <div className="rounded-2xl border-2 border-amber-500 bg-amber-900/15 p-6 text-center space-y-4">
          <div className="text-3xl font-black text-amber-200">{tx.packetaStale(st.bulk.code, shipDay(st.bulk.ship_date))}</div>
          <div className="text-gray-300">{st.count} parcel{st.count === 1 ? '' : 's'} scanned into it</div>
          <div className="flex flex-wrap justify-center gap-3">
            <button disabled={busy} onClick={() => answerStale(true)} className="px-8 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-2xl font-black">{tx.packetaStaleYes}</button>
            <button disabled={busy} onClick={() => answerStale(false)} className="px-8 py-4 rounded-2xl bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-2xl font-bold">{tx.packetaStaleNo}</button>
          </div>
          {last && !last.ok && <div className="text-red-400">{last.text}</div>}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-4 items-stretch">
            <div className="rounded-2xl bg-blue-900/30 border border-blue-700 px-8 py-6 min-w-[220px] text-center">
              <div className="text-6xl font-black">{st?.count ?? 0}</div>
              <div className="text-blue-200">scanned</div>
            </div>
            <div className="flex-1 rounded-2xl border border-gray-700 bg-[#1a1d27] p-4">
              <div className="text-gray-300 text-lg">Scan every Packeta parcel that goes with the courier.</div>
              {last && <div className={`mt-2 text-3xl font-black ${last.ok ? 'text-green-400' : 'text-red-400'}`}>{last.text}</div>}
              <div className="mt-3 flex gap-2">
                <button onClick={undo} className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600">Take the last one out</button>
              </div>
            </div>
          </div>

          {!!st?.problems?.length && (
            // 3.11: scanned while its round ran, and since then it must not go — take it out of the pile
            <div className="rounded-2xl border-2 border-red-600 bg-red-900/20 p-4 space-y-2">
              <div className="text-red-200 text-xl font-black">Take {st.problems.length === 1 ? 'this parcel' : `these ${st.problems.length} parcels`} out — {st.problems.length === 1 ? 'it' : 'they'} must not go:</div>
              {st.problems.map(p => (
                <div key={p.item_id} className="flex flex-wrap items-center gap-3">
                  <span className="text-gray-100">{p.why}</span>
                  <button onClick={() => takeOut(p)} className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white font-bold">Taken out</button>
                </div>
              ))}
            </div>
          )}

          {st && st.missing.length > 0 && (
            <div className="rounded-2xl border border-amber-700 bg-amber-900/15 p-4">
              <div className="text-amber-200 font-bold mb-1">{st.missing.length} Packeta parcel(s) not scanned yet — find them:</div>
              <div className="text-gray-200 text-sm">{st.missing.map(m => `${m.order_number}${m.processed_at ? ` (${dayHm(m.processed_at)})` : m.round_id ? ` (packed, round ${m.round_id})` : ''}`).join(', ')}</div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button disabled={busy || !st?.count || !!st?.problems?.length} onClick={() => close(false)}
              className="px-8 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-30 text-2xl font-black">
              {tx.packetaCourierTook(st?.count ?? 0)}</button>
            {st && st.missing.length > 0 && (
              <button disabled={busy || !st?.count} onClick={() => { if (window.confirm(`Hand over without the ${st.missing.length} parcel(s) not scanned? They are written on the handover.`)) close(true); }}
                className="px-6 py-4 rounded-2xl bg-amber-700 hover:bg-amber-600 disabled:opacity-30 text-lg font-bold">
                Courier took them — without the not-scanned ones</button>
            )}
          </div>
          <div className="text-gray-500 text-sm">The handover stays open until the courier takes it — "{backLabel}" keeps it, and more parcels can be scanned into it later.</div>

          {st && st.items.length > 0 && (
            <div className="text-sm text-gray-400">
              <div className="uppercase tracking-widest text-xs mb-1">Scanned</div>
              {st.items.slice(0, 60).map(i => <span key={i.id} className="inline-block mr-4">{i.order_number} <span className="text-gray-600">{i.by_person} {hm(i.at)}</span></span>)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
